
import { supabase } from "@/integrations/supabase/client";
import { ContentItem, ContentKind, Course } from "@/types/course";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { uploadFile, deleteFile, getFileUrl } from "@/services/fileStorageService";

// Helper type to avoid excessive type depth
type Safe<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

// Re-export the lesson management functions to maintain compatibility
export { 
  createLesson, 
  updateLesson,
  deleteLesson,
  reorderLessons 
} from "@/services/lessonManagement.service";

/**
 * Uploads a course image and updates the course record
 */
export async function uploadCourseImage(courseId: string, imageFile: File): Promise<string> {
  // Create the file path in the format: course-images/{courseId}/{filename}
  const folderPath = `course-images/${courseId}`;
  
  try {
    // First, check if there's an existing image to delete it
    const { data: course } = await supabase
      .from('courses')
      .select('image_url')
      .eq('id', courseId)
      .single();
      
    // If there's an existing image, delete it
    if (course?.image_url) {
      const existingFilePath = course.image_url.split('/').slice(-3).join('/');
      
      try {
        await deleteFile('course-content', existingFilePath);
      } catch (deleteErr) {
        console.error("Error deleting existing image:", deleteErr);
        // Continue with upload even if delete fails
      }
    }
    
    // Upload the new image
    const filePath = await uploadFile('course-content', folderPath, imageFile);
    
    // Get the public URL
    const publicUrl = await getFileUrl('course-content', filePath);
    
    // Update the course record with the new image URL
    const { error: updateError } = await supabase
      .from('courses')
      .update({ 
        image_url: publicUrl,
        has_pending_changes: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', courseId);
      
    if (updateError) throw updateError;
    
    return publicUrl;
  } catch (error) {
    console.error("Error uploading course image:", error);
    throw new Error("Failed to upload course image. Please try again.");
  }
}

/**
 * Removes a course image and updates the course record
 */
export async function removeCourseImage(courseId: string): Promise<void> {
  try {
    // Get the current image URL
    const { data: course } = await supabase
      .from('courses')
      .select('image_url')
      .eq('id', courseId)
      .single();
      
    if (!course?.image_url) {
      // No image to remove
      return;
    }
    
    // Extract the file path from the URL
    const filePath = course.image_url.split('/').slice(-3).join('/');
    
    // Delete the file from storage
    await deleteFile('course-content', filePath);
    
    // Update the course record
    const { error: updateError } = await supabase
      .from('courses')
      .update({ 
        image_url: null,
        has_pending_changes: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', courseId);
      
    if (updateError) throw updateError;
  } catch (error) {
    console.error("Error removing course image:", error);
    throw new Error("Failed to remove course image. Please try again.");
  }
}

/**
 * Adds a new content item to a lesson
 */
export async function addContentItem(
  lesson_id: string,
  title: string,
  description: string | null,
  content_type: string,
  url: string | null,
  file_path: string | null,
  is_required: boolean,
  order_index: number,
  duration: number | null
): Promise<string> {
  const { data, error } = await supabase
    .from('course_content_items')
    .insert({
      lesson_id,
      title,
      description,
      content_type,
      url,
      file_path,
      is_required,
      order_index,
      duration
    })
    .select('id')
    .single();

  if (error) throw error;
  
  // Mark the associated course as having pending changes
  await markCourseChanges(lesson_id);
  
  return data.id;
}

/**
 * Updates an existing content item
 */
export async function updateContentItem(id: string, updates: {
  title?: string;
  description?: string | null;
  content_type?: string;
  url?: string | null;
  file_path?: string | null;
  is_required?: boolean;
  order_index?: number;
  duration?: number | null;
}): Promise<void> {
  // Get the lesson_id first to mark the course as changed
  const { data: item, error: fetchError } = await supabase
    .from('course_content_items')
    .select('lesson_id')
    .eq('id', id)
    .single();
    
  if (fetchError) throw fetchError;
  
  const { error } = await supabase
    .from('course_content_items')
    .update(updates)
    .eq('id', id);

  if (error) throw error;
  
  // Mark the course as having pending changes
  if (item?.lesson_id) {
    await markCourseChanges(item.lesson_id);
  }
}

/**
 * Deletes a content item
 */
export async function deleteContentItem(id: string): Promise<void> {
  // First get the file path and lesson_id if they exist
  const { data: item, error: fetchError } = await supabase
    .from('course_content_items')
    .select('file_path, lesson_id')
    .eq('id', id)
    .single();
    
  if (fetchError) throw fetchError;
  
  // Delete from database first
  const { error } = await supabase
    .from('course_content_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
  
  // If there was a file associated, delete it from storage
  if (item?.file_path) {
    try {
      const { error: storageError } = await supabase.storage
        .from('course-content')
        .remove([item.file_path]);
        
      if (storageError) {
        console.error("Error removing file from storage:", storageError);
      }
    } catch (storageErr) {
      console.error("Exception removing file from storage:", storageErr);
      // Don't throw the error as the database record was already deleted
    }
  }
  
  // Mark the course as having pending changes
  if (item?.lesson_id) {
    await markCourseChanges(item.lesson_id);
  }
}

/**
 * Gets all content items for a specific lesson
 */
export async function getContentItemsByLessonId(lessonId: string): Promise<ContentItem[]> {
  const { data, error } = await supabase
    .from('course_content_items')
    .select('*')
    .eq('lesson_id', lessonId)
    .order('order_index');

  if (error) throw error;
  
  // Transform database records to match our ContentItem interface
  return (data || []).map(item => ({
    id: item.id,
    title: item.title,
    type: (item.content_type as ContentKind) || 'reading',
    content_type: item.content_type,
    url: item.url || "",
    file_path: item.file_path || undefined,
    lesson_id: item.lesson_id,
    is_required: item.is_required,
    duration: item.duration || undefined,
    description: item.description || undefined,
    order_index: item.order_index,
    completed: false, // Default value since DB doesn't store this
    created_at: item.created_at,
    updated_at: item.updated_at
  }));
}

/**
 * Reorders content items within a lesson
 */
export async function reorderContentItems(lessonId: string, orderedIds: string[]): Promise<void> {
  // Create an array of promises to execute
  const updates = orderedIds.map((id, index) => {
    return supabase
      .from('course_content_items')
      .update({ order_index: index })
      .eq('id', id)
      .eq('lesson_id', lessonId)
      .select('id')
      .single(); // Use single() to return a Promise
  });
  
  // Execute all updates in parallel
  await Promise.all(updates);
  
  // Mark the course as having pending changes
  await markCourseChanges(lessonId);
}

/**
 * Get course types
 */
export async function getCourseTypes(): Promise<any[]> {
  const { data, error } = await supabase
    .from('course_types')
    .select('*')
    .order('name');
  
  if (error) throw error;
  return data || [];
}

/**
 * Get all courses with additional information
 */
export async function getCourses(): Promise<Course[]> {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      course_types(name)
    `)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  
  // Format and explicitly cast the data to match our Course interface
  const courses = data?.map(course => ({
    ...course,
    type_name: course.course_types?.name || null,
    status: course.status || 'draft',
    has_pending_changes: course.has_pending_changes || false,
    last_published: course.last_published || null
  })) as Safe<Course[]>;
  
  return courses;
}

/**
 * Get a specific course by ID
 */
export async function getCourseById(courseId: string): Promise<Course | null> {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      course_types(name)
    `)
    .eq('id', courseId)
    .maybeSingle() as PostgrestSingleResponse<any>;
  
  if (error) throw error;
  
  if (!data) return null;
  
  return {
    ...data,
    type_name: data.course_types?.name || null,
    status: data.status || 'draft',
    has_pending_changes: data.has_pending_changes || false,
    last_published: data.last_published || null
  } as Safe<Course>;
}

/**
 * Module management functions
 */
export async function createModule(
  courseId: string, 
  title: string, 
  description: string | null, 
  orderIndex: number
): Promise<any> {
  const { data, error } = await supabase
    .from('course_modules')
    .insert({
      course_id: courseId,
      title,
      description,
      order_index: orderIndex
    })
    .select('*')
    .single();
  
  if (error) throw error;
  
  // Mark the course as having pending changes
  await markCourseHasPendingChanges(courseId, true);
  
  return data;
}

export async function updateModule(id: string, updates: {
  title?: string;
  description?: string | null;
}): Promise<any> {
  // First get the course_id to mark it as changed
  const { data: module, error: fetchError } = await supabase
    .from('course_modules')
    .select('course_id')
    .eq('id', id)
    .single();
    
  if (fetchError) throw fetchError;
  
  const { data, error } = await supabase
    .from('course_modules')
    .update(updates)
    .eq('id', id)
    .select('*')
    .single();
  
  if (error) throw error;
  
  // Mark the course as having pending changes
  if (module?.course_id) {
    await markCourseHasPendingChanges(module.course_id, true);
  }
  
  return data;
}

export async function deleteModule(id: string): Promise<void> {
  // First get the course_id to mark it as changed
  const { data: module, error: fetchError } = await supabase
    .from('course_modules')
    .select('course_id')
    .eq('id', id)
    .single();
    
  if (fetchError) throw fetchError;
  
  const { error } = await supabase
    .from('course_modules')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
  
  // Mark the course as having pending changes
  if (module?.course_id) {
    await markCourseHasPendingChanges(module.course_id, true);
  }
}

/**
 * Reorders modules within a course
 */
export async function reorderModules(courseId: string, orderedIds: string[]): Promise<void> {
  // Create an array of promises to execute
  const updates = orderedIds.map((id, index) => {
    return supabase
      .from('course_modules')
      .update({ order_index: index })
      .eq('id', id)
      .select('id')
      .single(); // Use single() to return a Promise
  });
  
  // Execute all updates in parallel
  await Promise.all(updates);
  
  // Mark the course as having pending changes
  await markCourseHasPendingChanges(courseId, true);
}

/**
 * Helper function to mark a course as having pending changes
 * when changes are made to a lesson
 */
async function markCourseChanges(lessonId: string): Promise<void> {
  try {
    // Get the module ID from the lesson
    const { data: lesson, error: lessonError } = await supabase
      .from('course_lessons')
      .select('module_id')
      .eq('id', lessonId)
      .single();
    
    if (lessonError || !lesson?.module_id) return;
    
    // Mark the course as changed
    await markCourseChangesFromModule(lesson.module_id);
  } catch (error) {
    console.error("Error marking course changes:", error);
  }
}

/**
 * Helper function to mark a course as having pending changes
 * when changes are made to a module
 */
async function markCourseChangesFromModule(moduleId: string): Promise<void> {
  try {
    // Get the course ID from the module
    const { data: module, error: moduleError } = await supabase
      .from('course_modules')
      .select('course_id')
      .eq('id', moduleId)
      .single();
    
    if (moduleError || !module?.course_id) return;
    
    // Mark the course as having pending changes
    await markCourseHasPendingChanges(module.course_id, true);
  } catch (error) {
    console.error("Error marking course changes from module:", error);
  }
}

/**
 * Helper function to directly mark a course as having pending changes
 */
async function markCourseHasPendingChanges(courseId: string, hasPendingChanges: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('courses')
      .update({ 
        has_pending_changes: hasPendingChanges,
        updated_at: new Date().toISOString()
      })
      .eq('id', courseId);
      
    if (error) {
      console.error("Error marking course pending changes:", error);
    }
  } catch (error) {
    console.error("Exception marking course pending changes:", error);
  }
}

/**
 * Publishes all course changes to learner-facing content
 */
export async function publishCourseChanges(courseId: string): Promise<boolean> {
  try {
    // 1. Update course status to reflect it's been published
    const { error: updateError } = await supabase
      .from('courses')
      .update({ 
        updated_at: new Date().toISOString(),
        status: 'published',
        has_pending_changes: false,
        last_published: new Date().toISOString()
      })
      .eq('id', courseId);

    if (updateError) throw updateError;

    // 2. Refresh course counts (modules_count and lessons_count)
    await refreshCourseCounts(courseId);

    // Success
    return true;
  } catch (error) {
    console.error("Error publishing course changes:", error);
    return false;
  }
}

/**
 * Gets courses with pending changes that need to be published
 */
export async function getCoursesWithPendingChanges(): Promise<Course[]> {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      course_types(name)
    `)
    .eq('has_pending_changes', true)
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  
  // Format and explicitly cast the data to match our Course interface
  return (data || []).map(course => ({
    ...course,
    type_name: course.course_types?.name || null,
    status: course.status || 'draft',
    has_pending_changes: true,
    last_published: course.last_published || null
  })) as Safe<Course[]>;
}

/**
 * Gets draft courses that haven't been published yet
 */
export async function getDraftCourses(): Promise<Course[]> {
  const { data, error } = await supabase
    .from('courses')
    .select(`
      *,
      course_types(name)
    `)
    .eq('status', 'draft')
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  
  // Format and explicitly cast the data to match our Course interface
  return (data || []).map(course => ({
    ...course,
    type_name: course.course_types?.name || null,
    status: 'draft',
    has_pending_changes: course.has_pending_changes || false,
    last_published: course.last_published || null
  })) as Safe<Course[]>;
}

/**
 * Refreshes course counts for modules and lessons
 */
export async function refreshCourseCounts(courseId: string): Promise<void> {
  try {
    // Get module count for this course
    const { count: moduleCount, error: moduleError } = await supabase
      .from('course_modules')
      .select('*', { count: 'exact', head: true })
      .eq('course_id', courseId);

    if (moduleError) throw moduleError;

    // First get the module IDs for this course
    const { data: moduleData, error: moduleIdsError } = await supabase
      .from('course_modules')
      .select('id')
      .eq('course_id', courseId);

    if (moduleIdsError) throw moduleIdsError;

    // Extract the module IDs into an array
    const moduleIds = moduleData.map(module => module.id);

    // Get lesson count for modules in this course - only if we have modules
    let lessonCount = 0;
    
    if (moduleIds.length > 0) {
      const { count: lessonCountResult, error: lessonError } = await supabase
        .from('course_lessons')
        .select('*', { count: 'exact', head: true })
        .in('module_id', moduleIds);

      if (lessonError) throw lessonError;
      
      lessonCount = lessonCountResult || 0;
    }

    // Update the course with these counts
    const { error: updateError } = await supabase
      .from('courses')
      .update({
        modules_count: moduleCount || 0,
        lessons_count: lessonCount || 0
      })
      .eq('id', courseId);

    if (updateError) throw updateError;
  } catch (error) {
    console.error("Error refreshing course counts:", error);
    throw error;
  }
}

/**
 * Add missing pagination argument in functions being called from CoursesManagementPage and ModulesManagementPage
 */
export async function listCourses(page: number, perPage: number): Promise<{ data: Course[], count: number }> {
  const from = (page - 1) * perPage;
  const to = from + perPage - 1;
  
  const { data, error, count } = await supabase
    .from('courses')
    .select(`
      *,
      course_types(name)
    `, { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);
    
  if (error) throw error;
  
  const courses = (data || []).map(course => ({
    ...course,
    type_name: course.course_types?.name || null,
    status: course.status || 'draft',
    has_pending_changes: course.has_pending_changes || false,
    last_published: course.last_published || null
  })) as Safe<Course[]>;
  
  return { data: courses, count: count || 0 };
}
